import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './CaptureDocument.css';
import {
  ArrowForwardRounded,
  DocumentScannerOutlined,
  Refresh
} from '@mui/icons-material';
import UploadScreen from './UploadScreen';
import { useIdentification } from './IdentificationContext';
import config from './config';
import CameraCapture from './components/CameraCapture';
import {
  BackButton,
  EnterpriseButton,
  EnterpriseCard,
  EnterpriseHero,
  EnterpriseShell,
  EnterpriseTopBar,
  WarningBanner
} from './ui/EnterpriseLayout';

function CaptureDocument() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [showWarning, setShowWarning] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState(10);
  const [countdown, setCountdown] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [cameraKey, setCameraKey] = useState(0); // Force re-mount for refresh
  const { identificationId } = useIdentification();

  useEffect(() => {
    if (!showWarning) return;
    
    const interval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 60000);

    return () => clearInterval(interval);
  }, [showWarning]);

  const handleCapture = (blob) => {
    // Convert blob to data URL for preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setCapturedImage(reader.result);
    };
    reader.readAsDataURL(blob);
  };

  const retryCapture = () => {
    setCapturedImage(null);
    setCountdown(null);
    setCameraKey(prev => prev + 1); // Force re-mount
  };

  const refreshCamera = () => {
    setCameraKey(prev => prev + 1); // Force re-mount
  };

  const confirmCapture = async () => {
    if (!identificationId) {
      console.error('No identification ID available');
      alert('Error: Please start over from the beginning.');
      return;
    }

    // Start upload process
    setUploading(true);

    try {
      // Convert base64 to Blob
      const base64Response = await fetch(capturedImage);
      const blob = await base64Response.blob();

      const formData = new FormData();
      formData.append('file', blob, 'document.jpg');
      formData.append('documentType', 'DOCUMENT');

      const response = await fetch(`${config.API_BASE_URL}/identifications/${identificationId}/files`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to upload document');
      }

      const fileData = await response.json();
      console.log('Document uploaded successfully:', fileData);
      
      // UploadScreen will call handleUploadComplete when done
    } catch (error) {
      console.error('Error uploading document:', error);
      setUploading(false);
      alert('Failed to upload document. Please try again.');
    }
  };

  const handleUploadComplete = () => {
    // Navigate to ID verification screen with step 1 complete
    navigate('/id-verification?step=1-complete');
  };

  if (uploading) {
    return <UploadScreen onComplete={handleUploadComplete} />;
  }

  return (
    <EnterpriseShell>
      <EnterpriseTopBar
        right={
          <>
            <BackButton
              onClick={() => {
                navigate('/id-verification');
              }}
            />
            <span className="vt-chip vt-chip--subtle">ID verification</span>
          </>
        }
      />

      <EnterpriseHero
        title="Capture your document"
        subtitle="Position the document inside the frame. Ensure details are clear and readable."
      />

      <EnterpriseCard>
        {showWarning && (
          <div style={{ marginBottom: 14 }}>
            <WarningBanner onClose={() => setShowWarning(false)}>
              For security reasons this session will expire in {timeRemaining} minutes.
            </WarningBanner>
          </div>
        )}

          {capturedImage ? (
            <>
              <div className="vt-cardHeader">
                <div className="vt-iconTile" aria-hidden="true">
                  <DocumentScannerOutlined className="vt-iconTileIcon" />
                </div>
                <div>
                  <h2 className="vt-cardTitle">Review</h2>
                  <p className="vt-cardDescription">Are your details clear and readable?</p>
                </div>
              </div>
              <div className="captured-image-container">
                <img 
                  src={capturedImage} 
                  alt="Captured document" 
                  className="captured-image"
                />
              </div>
              <div className="review-actions">
                <EnterpriseButton variant="secondary" type="button" onClick={retryCapture}>
                  <Refresh className="vt-btnIcon" />
                  No, retry
                </EnterpriseButton>
                <EnterpriseButton variant="primary" type="button" onClick={confirmCapture} disabled={uploading}>
                  <span>{uploading ? 'Uploading…' : "Yes, they're clear"}</span>
                  <ArrowForwardRounded className="vt-btnIcon" />
                </EnterpriseButton>
              </div>
            </>
          ) : (
            <>
              <div className="camera-container" style={{ marginBottom: 14 }}>
                <div className="camera-wrapper">
                  <CameraCapture
                    key={cameraKey}
                    mode="document"
                    facingMode="environment"
                    onCapture={handleCapture}
                    showCameraSelector={false}
                    countdown={countdown}
                    onCountdownChange={setCountdown}
                    videoRef={videoRef}
                  />
                </div>
              </div>

              <div className="capture-actions">
                <EnterpriseButton
                  variant="primary"
                  type="button"
                  onClick={() => setCountdown(3)}
                  disabled={countdown !== null}
                >
                  Take photo
                  <ArrowForwardRounded className="vt-btnIcon" />
                </EnterpriseButton>
                <EnterpriseButton
                  variant="secondary"
                  type="button"
                  onClick={refreshCamera}
                  disabled={countdown !== null}
                >
                  <Refresh className="vt-btnIcon" />
                  Refresh camera
                </EnterpriseButton>
              </div>
            </>
          )}
      </EnterpriseCard>
    </EnterpriseShell>
  );
}

export default CaptureDocument;
